import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Opportunity, UserProfile, HarvestCandidate } from "../../types";
import { buildHarvestCandidates, promoteCandidates } from "../../services/harvestService";
import { detectResidualWarnings } from "../../utils/sanitizeClient";
import {
  BookOpen,
  Sparkles,
  X,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  FileText,
  HelpCircle,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

export interface HarvestLibraryModalProps {
  opportunity: Opportunity;
  user: UserProfile | null;
  isOpen: boolean;
  onClose: () => void;
  onHarvestCompleted?: (createdIds: string[]) => void;
}

export const HarvestLibraryModal: React.FC<HarvestLibraryModalProps> = ({
  opportunity,
  user,
  isOpen,
  onClose,
  onHarvestCompleted,
}) => {
  const [candidates, setCandidates] = useState<HarvestCandidate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPromoting, setIsPromoting] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;

    buildHarvestCandidates(opportunity.id, opportunity.client)
      .then((items) => {
        if (isMounted) {
          setCandidates(items);
          setIsLoading(false);
          if (items.length > 0) {
            setExpandedId(items[0]?.id || null);
          }
        }
      })
      .catch((err) => {
        if (isMounted) {
          console.error("Failed to build harvest candidates:", err);
          setErrorMessage(
            err instanceof Error ? err.message : "Failed to load candidates for harvest.",
          );
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, opportunity.id, opportunity.client]);

  const toggleSelect = (id: string) => {
    setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, selected: !c.selected } : c)));
  };

  const updateCandidateTitle = (id: string, newTitle: string) => {
    setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, title: newTitle } : c)));
  };

  const updateCandidateBody = (id: string, newBody: string) => {
    setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, sanitizedBody: newBody } : c)));
  };

  const handlePromote = async () => {
    if (!user) return;
    const selected = candidates.filter((c) => c.selected);
    if (selected.length === 0) return;

    setIsPromoting(true);
    setErrorMessage(null);

    try {
      const createdIds = await promoteCandidates(
        opportunity.id,
        opportunity.client,
        selected,
        user,
      );

      setSuccessMessage(
        `Successfully harvested ${createdIds.length} response(s) into the Answer Library with 'Review Due' status!`,
      );

      if (onHarvestCompleted) {
        onHarvestCompleted(createdIds);
      }

      setTimeout(() => {
        onClose();
      }, 1800);
    } catch (err) {
      console.error("Harvest promotion error:", err);
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to promote candidates to Answer Library.",
      );
    } finally {
      setIsPromoting(false);
    }
  };

  const selectedCount = candidates.filter((c) => c.selected).length;
  const duplicateCount = candidates.filter((c) => (c.similarityScore || 0) >= 0.8).length;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs"
        />

        {/* Modal Dialog */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative z-10 w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-teal-100 p-2 text-[#2ba5b5] dark:bg-teal-950/60 dark:text-teal-300">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <span>Harvest Knowledge to Living Library</span>
                  <span className="rounded-full bg-amber-100 px-2 py-0.2 text-[10px] font-semibold text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                    Review Due
                  </span>
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">
                  {opportunity.client} — {opportunity.title}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Banner Strip */}
          <div className="bg-slate-50/80 px-6 py-2.5 border-b border-slate-200/80 dark:border-slate-800 dark:bg-slate-950/40 text-xs flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400">
              <span>
                <strong>{candidates.length}</strong> Candidate{candidates.length !== 1 ? "s" : ""}
              </span>
              <span>•</span>
              <span className="text-[#2ba5b5] font-semibold">
                {selectedCount} Selected for Ingestion
              </span>
              {duplicateCount > 0 && (
                <>
                  <span>•</span>
                  <span className="text-amber-600 dark:text-amber-400">
                    {duplicateCount} Existing Variant{duplicateCount !== 1 ? "s" : ""} (Skipped)
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              <span>Client Name Auto-Sanitized</span>
            </div>
          </div>

          {/* Body Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            {successMessage && (
              <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-xs font-semibold text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                <span>{successMessage}</span>
              </div>
            )}

            {errorMessage && (
              <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200">
                <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
                <span>{errorMessage}</span>
              </div>
            )}

            {isLoading ? (
              <div className="py-16 text-center text-slate-400">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#2ba5b5]" />
                <p className="mt-3 text-xs font-medium">
                  Scanning proposal sections & SME responses...
                </p>
              </div>
            ) : candidates.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center dark:border-slate-800">
                <BookOpen className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600" />
                <p className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                  No harvestable responses found.
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  Ensure the proposal has drafted narrative sections or answered SME questions
                  before harvesting.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {candidates.map((c) => {
                  const warnings = detectResidualWarnings(c.sanitizedBody);
                  const isExpanded = expandedId === c.id;
                  const isDuplicate = (c.similarityScore || 0) >= 0.8;

                  return (
                    <div
                      key={c.id}
                      className={`rounded-xl border transition-all ${
                        c.selected
                          ? "border-teal-300 bg-teal-50/20 dark:border-teal-900/60 dark:bg-teal-950/10 shadow-2xs"
                          : "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 opacity-75"
                      }`}
                    >
                      {/* Candidate Card Header */}
                      <div className="flex items-start justify-between gap-3 p-3.5">
                        <div className="flex items-start gap-3 min-w-0">
                          <input
                            type="checkbox"
                            checked={c.selected}
                            onChange={() => toggleSelect(c.id)}
                            className="mt-1 rounded text-[#2ba5b5] focus:ring-[#2ba5b5]"
                          />
                          <div className="min-w-0 space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.2 text-[10px] font-bold ${
                                  c.sourceType === "draft_section"
                                    ? "bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300"
                                    : "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300"
                                }`}
                              >
                                {c.sourceType === "draft_section" ? (
                                  <FileText className="h-3 w-3" />
                                ) : (
                                  <HelpCircle className="h-3 w-3" />
                                )}
                                <span>
                                  {c.sourceType === "draft_section"
                                    ? "Draft Section"
                                    : "SME Answer"}
                                </span>
                              </span>

                              {isDuplicate && (
                                <span className="rounded-full bg-amber-100 px-2 py-0.2 text-[10px] font-semibold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                                  Similar to "{c.similarToTitle}" (
                                  {Math.round((c.similarityScore || 0) * 100)}%)
                                </span>
                              )}

                              {warnings.length > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.2 text-[10px] font-bold text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
                                  <AlertTriangle className="h-3 w-3" />
                                  <span>
                                    {warnings.length} PII / Format Warning
                                    {warnings.length !== 1 ? "s" : ""}
                                  </span>
                                </span>
                              )}
                            </div>

                            <input
                              type="text"
                              value={c.title}
                              onChange={(e) => updateCandidateTitle(c.id, e.target.value)}
                              className="w-full font-semibold text-xs text-slate-900 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-[#2ba5b5] focus:outline-hidden dark:text-white"
                            />

                            <p className="text-[11px] text-slate-400">By {c.author}</p>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : c.id)}
                          className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      </div>

                      {/* Expandable Preview & Edit */}
                      {isExpanded && (
                        <div className="border-t border-slate-100 p-3.5 space-y-3 dark:border-slate-800 text-xs">
                          {warnings.length > 0 && (
                            <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-2.5 text-[11px] text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
                              <p className="font-bold mb-1">
                                Human review recommended before ingestion:
                              </p>
                              <ul className="list-disc pl-4 space-y-0.5">
                                {warnings.map((w, idx) => (
                                  <li key={idx}>{w}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <div>
                            <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                              Sanitized Narrative (Editable):
                            </label>
                            <textarea
                              rows={4}
                              value={c.sanitizedBody}
                              onChange={(e) => updateCandidateBody(c.id, e.target.value)}
                              className="w-full rounded-lg border border-slate-200 bg-white p-2 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between border-t border-slate-200 bg-slate-50/80 px-6 py-3.5 dark:border-slate-800 dark:bg-slate-900/60">
            <button
              type="button"
              onClick={onClose}
              disabled={isPromoting}
              className="btn-animate rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handlePromote}
              disabled={isPromoting || selectedCount === 0}
              className="btn-animate inline-flex items-center gap-2 rounded-xl bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white shadow-2xs hover:bg-[#238a97] disabled:opacity-50"
            >
              {isPromoting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Harvesting to Library...</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  <span>
                    Harvest {selectedCount} Response{selectedCount !== 1 ? "s" : ""} as 'Review Due'
                  </span>
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
